import {z} from 'zod'

import {mapPartialPickup, partialPickupRawSchema} from 'src/work/partial-pickup-schema'

export const activeRentalRawSchema = z.object({
    project_id: z.string(),
    project_name: z.string(),
    order_number: z.string().nullable(),
    customer_id: z.string(),
    customer_name: z.string(),
    rep_name: z.string().nullable(),
    dispatch_yard_id: z.string().nullable(),
    job_id: z.string().nullable(),
    job_display_id: z.string().nullable(),
    job_name: z.string().nullable(),
    job_status: z.enum(['planned', 'active', 'completed', 'cancelled']).nullable(),
    sites: z.array(z.string()),
    equipment: z.array(
        z.object({
            rental_asset_type_name: z.string(),
            count: z.number(),
            unit_names: z.array(z.string()),
            is_rerent: z.boolean(),
            vendor_name: z.string().nullable(),
        }),
    ),
    unit_count: z.number(),
    on_rent_since: z.string(),
    planned_end_date: z.string().nullable(),
    past_end_date: z.boolean(),
    pickup: z
        .object({
            job_id: z.string(),
            display_id: z.string(),
            start_date: z.string().nullable(),
            confirmed: z.boolean(),
        })
        .nullable(),
    units_not_on_pickup: z.number(),
    partial_pickup: partialPickupRawSchema.nullable(),
})

export type ActiveRentalRaw = z.infer<typeof activeRentalRawSchema>

export const activeRentalSchema = activeRentalRawSchema.transform((raw) => ({
    projectId: raw.project_id,
    projectName: raw.project_name,
    orderNumber: raw.order_number,
    customerId: raw.customer_id,
    customerName: raw.customer_name,
    repName: raw.rep_name,
    dispatchYardId: raw.dispatch_yard_id,
    jobId: raw.job_id,
    jobDisplayId: raw.job_display_id,
    jobName: raw.job_name,
    jobStatus: raw.job_status,
    sites: raw.sites,
    equipment: raw.equipment.map((line) => ({
        rentalAssetTypeName: line.rental_asset_type_name,
        count: line.count,
        unitNames: line.unit_names,
        isRerent: line.is_rerent,
        vendorName: line.vendor_name,
    })),
    unitCount: raw.unit_count,
    onRentSince: raw.on_rent_since,
    plannedEndDate: raw.planned_end_date,
    pastEndDate: raw.past_end_date,
    pickup: raw.pickup
        ? {
              jobId: raw.pickup.job_id,
              displayId: raw.pickup.display_id,
              startDate: raw.pickup.start_date,
              confirmed: raw.pickup.confirmed,
          }
        : null,
    unitsNotOnPickup: raw.units_not_on_pickup,
    partialPickup: mapPartialPickup(raw.partial_pickup),
}))

export type ActiveRental = z.infer<typeof activeRentalSchema>
export type ActiveRentalEquipment = ActiveRental['equipment'][number]

export const confirmRentalsOutRequestSchema = z
    .object({rentalStintIds: z.array(z.string()).min(1)})
    .transform((input) => ({rental_stint_ids: input.rentalStintIds}))

export type ConfirmRentalsOutInput = z.input<typeof confirmRentalsOutRequestSchema>

export const confirmRentalsOutResponseRawSchema = z.object({confirmed_count: z.number()})

export const confirmRentalsOutResponseSchema = confirmRentalsOutResponseRawSchema.transform(
    (raw) => ({confirmedCount: raw.confirmed_count}),
)
